import classNames from "classnames";
import { useCallback } from "react";
import { NotesTableV3 } from "@/entities/notes-table-v3";
import { useContainerHeight } from "@/shared/hooks";
import { NotesManagerHeader } from "@/entities/notes-manager-header";
import { BookmarkTabsImproved } from "@/shared/Components";
import { NoNotePage } from "@/entities/no-note-page";
import { AddNoteButton } from "@/entities/add-note-button";
import { DeleteNotesModal } from "@/entities/delete-notes-modal";
import { getHeaderTitle, type NotesManagerProps } from "@/features/notes-manager/model";
import type { NotesTableItem } from "@/pages/NotesPage/model/types";
import styles from "./styles.module.scss";

export const Desktop = ({ notesManager, noteType, contactsPage = false }: NotesManagerProps) => {
  const { containerRef, containerHeight } = useContainerHeight();

  const containerHeightDivider = contactsPage ? 80 : 145;

  const { state, methods, tabsManager } = notesManager;

  const {
    columns,
    notesTableData,
    isLoading,
    selectedTableNotes,
    deleteModalIsOpen,
    deleteDisabled,
    emptyNotes,
    activeNote,
  } = state;
  const { rowSelection, handleClickRow, deleteSelected, handleCloseModal, handleOpenModal } =
    methods;

  const getRowClassName = useCallback(
    (record: NotesTableItem) => {
      return classNames({
        [styles["active-row"]]: record.id === activeNote?.id,
      });
    },
    [activeNote?.id],
  );

  return (
    <div className={styles.root} ref={containerRef}>
      <NotesManagerHeader
        headerTitle={getHeaderTitle(noteType)}
        disabledButton={deleteDisabled}
        onDeleteButtonClick={handleOpenModal}
        contactsPage={contactsPage}
      />
      {emptyNotes && !tabsManager.state.editorOpen ? (
        <NoNotePage noteType={noteType} addNoteHandler={methods.handleAddTempNote} />
      ) : (
        <div
          style={{
            height: `${containerHeight}px`,
            overflow: window.innerWidth > 500 ? "hidden" : "auto",
          }}
          className={styles["content-wrapper"]}
        >
          <div
            className={classNames(styles["table-container"], {
              [styles["table-container--editor"]]: tabsManager.state.editorOpen,
            })}
          >
            <NotesTableV3
              columns={columns}
              dataSource={notesTableData ?? []}
              rowKey={(row) => row.id}
              isPaginationVisible={false}
              scroll={{ y: containerHeight - containerHeightDivider, x: "max-content" }}
              rowSelection={rowSelection}
              onRow={(row) => ({
                onClick: () => handleClickRow(row),
              })}
              rowClassName={getRowClassName}
              virtual
              loading={isLoading}
            />
            <div
              className={classNames(styles["add-button"], {
                [styles["add-button--contacts"]]: contactsPage,
              })}
            >
              <AddNoteButton onClick={methods.handleAddTempNote} />
            </div>
          </div>
          <div
            className={classNames(styles["editor-wrapper"], {
              [styles["editor-wrapper--closed"]]: !tabsManager.state.editorOpen,
            })}
          >
            <BookmarkTabsImproved
              tabs={tabsManager.state.tabs}
              onTabChange={tabsManager.methods.handleTabChange}
              onTabClose={tabsManager.methods.handleCloseTab}
              activeKey={tabsManager.state.activeTab}
              isEditable
            />
          </div>
          <DeleteNotesModal
            notes={selectedTableNotes}
            onConfirm={deleteSelected}
            onCancel={handleCloseModal}
            open={deleteModalIsOpen}
            noteType={noteType}
          />
        </div>
      )}
    </div>
  );
};
